import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
  TablePagination,
  Stack,
} from "@mui/material";
import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { getAllOrders } from "../../../services/adminService";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await getAllOrders();
        // The API returns the array directly, not wrapped in a data property
        const ordersData = response || [];
        setOrders(ordersData);
        setFilteredOrders(ordersData);
        setLoading(false);
      } catch (err) {
        setError("Failed to load orders");
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  useEffect(() => {
    let filtered = orders || [];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((order) => {
        const name = `${order.userId?.firstName || ""} ${order.userId?.lastName || ""}`.toLowerCase();
        const email = order.userId?.email?.toLowerCase() || "";
        return name.includes(lower) || email.includes(lower);
      });
    }

    if (startDate && endDate) {
      const start = dayjs(startDate).startOf("day");
      const end = dayjs(endDate).endOf("day");

      filtered = filtered.filter((order) => {
        const created = dayjs(order.createdAt);
        return created.isAfter(start) && created.isBefore(end);
      });
    }

    setFilteredOrders(filtered);
    setPage(0);
  }, [searchTerm, startDate, endDate, orders]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <AdminDashboardLayout>
      <Box p={4}>
        <Typography variant="h4" fontWeight="bold" mb={3}>
          📦 Orders Overview
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3} alignItems="center">
          <TextField
            variant="outlined"
            fullWidth
            label="🔍 Search by Name or Email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 300 }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setSearchTerm("");
              setStartDate(null);
              setEndDate(null);
            }}
            sx={{ height: 56 }}
          >
            Reset Filters
          </Button>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Paper elevation={4} sx={{ borderRadius: 3, overflowX: "auto" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#000" }}>
                    <TableCell sx={{ color: "#fff" }}>#</TableCell>
                    <TableCell sx={{ color: "#fff" }}>User Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>User Email</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Plan</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Created At</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order, index) => (
                      <TableRow key={order._id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          {order.userId ? `${order.userId.firstName} ${order.userId.lastName}` : "N/A"}
                        </TableCell>
                        <TableCell>{order.userId?.email || "N/A"}</TableCell>
                        <TableCell>{order.planId || "Free Plan"}</TableCell>
                        <TableCell>{new Date(order.createdAt).toLocaleString()}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/admin/orders/${order._id}`)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>
    </AdminDashboardLayout>
  );
}